<script lang="ts">
  import { ShoppingBag, Eye, X } from '@lucide/svelte';

  interface Props {
    orders: any[];
    ordersQueryIsLoading: boolean;
  }

  let { orders = [], ordersQueryIsLoading }: Props = $props();

  let selectedOrder = $state<any>(null);

  function openOrder(order: any) {
    selectedOrder = order;
  }

  function closeOrder() {
    selectedOrder = null;
  }
</script>

<div class="space-y-6">
  <div class="space-y-1">
    <h2 class="text-3xl font-extrabold tracking-tight text-base-content">My Orders</h2>
    <p class="text-sm text-base-content/70">Track your order statuses and purchase history</p>
  </div>

  {#if ordersQueryIsLoading}
    <div class="space-y-4">
      <div class="skeleton h-12 w-full"></div>
      <div class="skeleton h-16 w-full"></div>
      <div class="skeleton h-16 w-full"></div>
    </div>
  {:else if !orders || orders.length === 0}
    <div class="py-20 text-center border border-dashed border-base-300 rounded-3xl bg-base-100 flex flex-col items-center justify-center space-y-4">
      <div class="w-12 h-12 rounded-full bg-base-200 flex items-center justify-center text-base-content/40">
        <ShoppingBag class="w-6 h-6" />
      </div>
      <div>
        <p class="text-base-content/50 font-medium">You have not placed any orders yet.</p>
        <p class="text-xs text-base-content/40 mt-1">Spiritual products purchased from the storefront will appear here.</p>
      </div>
      <a href="/products" class="btn btn-accent btn-sm rounded-xl font-bold uppercase tracking-wider">
        Browse Storefront
      </a>
    </div>
  {:else}
    <!-- Desktop Table View -->
    <div class="hidden md:block bg-base-100 border border-base-300 rounded-3xl overflow-hidden shadow-sm">
      <div class="overflow-x-auto">
        <table class="table table-zebra table-md w-full">
          <thead>
            <tr class="bg-base-200/50 text-xs font-semibold text-base-content/70 uppercase tracking-wider border-b border-base-300">
              <th>Order ID</th>
              <th>Date</th>
              <th>Items</th>
              <th>Total</th>
              <th>Status</th>
              <th class="text-right">Actions</th>
            </tr>
          </thead>
          <tbody class="text-base-content/95">
            {#each orders as order}
              <tr class="hover border-b border-base-200">
                <td class="font-mono text-xs font-bold text-accent">
                  #{order.id.slice(-8).toUpperCase()}
                </td>
                <td class="text-xs font-medium text-base-content/80">
                  {new Date(order.createdAt).toLocaleDateString(undefined, { dateStyle: 'medium' })}
                </td>
                <td class="text-xs text-base-content/70 font-semibold">
                  {order.items?.length || 0} {order.items?.length === 1 ? 'item' : 'items'}
                </td>
                <td class="font-bold text-base-content">
                  <span class="text-xs font-semibold">Rs.</span> {Number(order.total).toFixed(2)}
                </td>
                <td>
                  <span class="badge badge-sm badge-outline font-bold uppercase tracking-wider {
                    order.status === 'PENDING' ? 'badge-warning text-warning-content' :
                    order.status === 'PROCESSING' ? 'badge-info text-info-content' :
                    order.status === 'SHIPPED' ? 'badge-primary text-primary-content' :
                    order.status === 'DELIVERED' ? 'badge-success text-success-content' :
                    'badge-neutral'
                  }">
                    {order.status}
                  </span>
                </td>
                <td class="text-right">
                  <button 
                    onclick={() => openOrder(order)} 
                    class="btn btn-ghost btn-xs text-accent font-bold flex items-center gap-1.5 ml-auto cursor-pointer"
                  >
                    <Eye class="w-3.5 h-3.5" /> Details
                  </button>
                </td>
              </tr>
            {/each}
          </tbody>
        </table>
      </div>
    </div>

    <!-- Mobile Card View -->
    <div class="md:hidden space-y-4">
      {#each orders as order}
        <!-- svelte-ignore a11y_click_events_have_key_events -->
        <!-- svelte-ignore a11y_no_noninteractive_element_interactions -->
        <div 
          class="card bg-base-100 border border-base-300 rounded-2xl p-4 shadow-sm hover:border-primary/30 transition-all duration-300 space-y-3 cursor-pointer"
          onclick={() => openOrder(order)}
        >
          <!-- Card Header (Order ID & Status Badge) -->
          <div class="flex items-center justify-between">
            <span class="font-mono text-xs font-black text-accent">
              #{order.id.slice(-8).toUpperCase()}
            </span>
            <span class="badge badge-xs badge-outline font-black uppercase tracking-wider {
              order.status === 'PENDING' ? 'badge-warning text-warning-content' :
              order.status === 'PROCESSING' ? 'badge-info text-info-content' :
              order.status === 'SHIPPED' ? 'badge-primary text-primary-content' :
              order.status === 'DELIVERED' ? 'badge-success text-success-content' :
              'badge-neutral'
            }">
              {order.status}
            </span>
          </div>

          <!-- Checkout Date & Items -->
          <div class="grid grid-cols-2 gap-2 text-[11px] pt-1">
            <div>
              <span class="block text-[9px] font-bold text-base-content/40 uppercase tracking-wider mb-0.5">Order Date</span>
              <span class="font-semibold text-base-content/75 block">
                {new Date(order.createdAt).toLocaleDateString(undefined, { dateStyle: 'medium' })}
              </span>
            </div>
            <div>
              <span class="block text-[9px] font-bold text-base-content/40 uppercase tracking-wider mb-0.5">Volume</span>
              <span class="font-bold text-base-content block">
                {order.items?.length || 0} {order.items?.length === 1 ? 'item' : 'items'}
              </span>
            </div>
          </div>

          <!-- Total & Details Trigger Button -->
          <div class="grid grid-cols-2 gap-2 pt-2.5 border-t border-base-200 text-[11px] items-center">
            <div>
              <span class="block text-[9px] font-bold text-base-content/40 uppercase tracking-wider mb-0.5">Total paid</span>
              <span class="font-black text-xs text-accent">Rs. {Number(order.total).toFixed(2)}</span>
            </div>
            <div class="text-right">
              <button 
                onclick={(e) => { e.stopPropagation(); openOrder(order); }} 
                class="btn btn-ghost btn-xs text-accent font-bold px-2.5 h-6 min-h-6 rounded-md text-[10px]"
              >
                Details
              </button>
            </div>
          </div>
        </div>
      {/each}
    </div>
  {/if}
</div>

<!-- Order Details Modal -->
{#if selectedOrder}
  <dialog class="modal modal-open z-50">
    <div class="modal-box bg-base-100 border border-base-300 shadow-2xl rounded-3xl max-w-2xl w-full p-6 md:p-8 space-y-6 relative">
      
      <!-- Close Button -->
      <button 
        onclick={closeOrder} 
        class="btn btn-ghost btn-circle btn-sm text-base-content/60 hover:bg-base-200 absolute right-4 top-4 flex items-center justify-center cursor-pointer"
        aria-label="Close dialog"
      >
        <X class="w-5 h-5" />
      </button>

      <!-- Header Info -->
      <div class="border-b border-base-200 pb-4 space-y-1.5">
        <h3 class="font-black text-xl text-base-content flex items-center gap-2">
          Order Details 
          <span class="font-mono text-sm text-accent">#{selectedOrder.id.slice(-8).toUpperCase()}</span>
        </h3>
        <p class="text-xs text-base-content/50">
          Placed on {new Date(selectedOrder.createdAt).toLocaleString(undefined, { dateStyle: 'long', timeStyle: 'short' })}
        </p>
      </div>

      <!-- Items List -->
      <div class="space-y-4">
        <h4 class="text-xs font-bold uppercase tracking-wider text-base-content/65">Order Items</h4>
        <div class="space-y-3 max-h-48 overflow-y-auto pr-2">
          {#each selectedOrder.items || [] as item}
            <div class="flex items-center justify-between p-3 bg-base-200/50 rounded-2xl border border-base-300/40">
              <div class="min-w-0 flex-1">
                <p class="text-sm font-bold text-base-content truncate">{item.product?.name || 'Spiritual Item'}</p>
                <p class="text-xs text-base-content/50 mt-0.5">
                  Rs. {Number(item.price).toFixed(2)} × {item.quantity}
                </p>
              </div>
              <div class="text-sm font-extrabold text-base-content pl-4 shrink-0">
                Rs. {(Number(item.price) * item.quantity).toFixed(2)}
              </div>
            </div>
          {/each}
        </div>
      </div>

      <!-- Address / Customer Info -->
      <div class="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4 border-t border-base-200">
        <div class="space-y-2">
          <h4 class="text-xs font-bold uppercase tracking-wider text-base-content/65">Shipping Address</h4>
          <p class="text-xs leading-relaxed text-base-content/80 font-medium">
            {selectedOrder.address || 'No shipping address provided.'}
          </p>
        </div>
        <div class="space-y-2">
          <h4 class="text-xs font-bold uppercase tracking-wider text-base-content/65">Contact Details</h4>
          <p class="text-xs text-base-content/80 font-medium">
            Phone: {selectedOrder.contactNo || 'N/A'}
          </p>
          <p class="text-xs text-base-content/85 font-semibold mt-1">
            Status: 
            <span class="badge badge-sm badge-outline font-black uppercase tracking-wider ml-1 {
              selectedOrder.status === 'PENDING' ? 'badge-warning text-warning-content' :
              selectedOrder.status === 'PROCESSING' ? 'badge-info text-info-content' :
              selectedOrder.status === 'SHIPPED' ? 'badge-primary text-primary-content' :
              selectedOrder.status === 'DELIVERED' ? 'badge-success text-success-content' :
              'badge-neutral'
            }">{selectedOrder.status}</span>
          </p>
        </div>
      </div>

      <!-- Total summary -->
      <div class="border-t border-base-200 pt-4 flex justify-between items-center bg-base-200/30 p-4 rounded-2xl">
        <span class="text-xs font-bold uppercase tracking-wider text-base-content/60">Total Paid</span>
        <span class="text-xl font-black text-base-content">
          Rs. {Number(selectedOrder.total).toFixed(2)}
        </span>
      </div>

      <!-- Close dialog footer action -->
      <div class="modal-action mt-2">
        <button onclick={closeOrder} class="btn btn-outline btn-sm rounded-xl font-bold cursor-pointer">
          Close Details
        </button>
      </div>
    </div>
    <button class="modal-backdrop bg-black/60 cursor-default" onclick={closeOrder} aria-label="Close dialog"></button>
  </dialog>
{/if}
